import { useEffect, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type { components } from "@oathera/contract/api";
import { useDataQuality } from "../../state/data-quality.tsx";

/**
 * The Overview's own ingest-health strip (design 12.2: "ingest health strip"). It summarises the
 * overview response's `ingest_health` — a contract `DataQuality` — as queue/ingest facts the
 * operator reads at a glance: silent sources, drops in range, sampling, clock skew, revocation-race
 * acceptances, and sequence gaps.
 *
 * This is NOT a duplicate of the shell's persistent data-quality strip. The shell strip is the
 * always-on reminder "a chart is never read without knowing what might be missing"; this is the
 * Overview's focused ingest view with its own layout and drill-downs. To keep the two consistent
 * rather than contradictory, this component also REPORTS the response's facts into the shared
 * data-quality context, so the persistent strip reflects the same numbers (design 12.2, NFR-4).
 *
 * Every item is icon + text (design 12.4). Counts the response marks as sampled are shown as such.
 * Where the response does not provide a signal, the item reads an honest "unknown" — never a green
 * "all clear" (design 12.3).
 */

type DataQuality = components["schemas"]["DataQuality"];

type ItemSeverity = "ok" | "info" | "warn" | "unknown";

interface Item {
  readonly key: string;
  readonly icon: string;
  readonly text: string;
  readonly severity: ItemSeverity;
  /** Optional drill-down target. */
  readonly to?: string;
}

export function IngestHealthStrip(props: { ingest: DataQuality | undefined | null }): ReactNode {
  // A stubbed or partial response can omit `ingest_health` entirely; normalize to a complete,
  // honest-zero DataQuality so a missing field never throws (e.g. reading `.gaps` of undefined).
  const ingest = normalizeDataQuality(props.ingest);
  const navigate = useNavigate();
  const { report } = useDataQuality();

  // Keep the persistent shell strip consistent with what the overview reports. We translate the
  // response's single `drops_in_range` into the strip's per-stage shape under "pipeline" (the
  // response does not break drops down by stage, so we do not invent a split). `report` is a stable
  // callback, and the facts are plain numbers, so this re-reports only when a number actually
  // changes — it does not loop on the context value being recreated.
  const gapCount = (ingest.gaps ?? []).length;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: ingest.drops_in_range },
      sampling: { active: ingest.sampling_in_effect, rate: null, exact: !ingest.sampling_in_effect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, ingest.drops_in_range, ingest.sampling_in_effect, gapCount]);

  const items: Item[] = [
    silentItem(ingest),
    staleItem(ingest),
    dropsItem(ingest),
    samplingItem(ingest),
  ];
  const skew = skewItem(ingest);
  if (skew !== null) items.push(skew);
  const revoke = revocationItem(ingest);
  if (revoke !== null) items.push(revoke);
  const gap = gapItem(ingest);
  if (gap !== null) items.push(gap);

  const degraded = items.some((i) => i.severity === "warn");

  return (
    <section className="ov-ingest" aria-label="Ingest health">
      <h2 className="ov-section__title">Ingest health</h2>
      <div className="ov-ingest__strip" data-degraded={degraded} role="group" aria-label="Ingest health signals">
        {items.map((item) =>
          item.to === undefined ? (
            <span key={item.key} className="ov-ingest__item" data-severity={item.severity}>
              <i className="ov-ingest__icon" aria-hidden="true">
                {item.icon}
              </i>
              <span>{item.text}</span>
            </span>
          ) : (
            <button
              key={item.key}
              type="button"
              className="ov-ingest__item ov-ingest__item--link"
              data-severity={item.severity}
              onClick={() => navigate(item.to as string)}
            >
              <i className="ov-ingest__icon" aria-hidden="true">
                {item.icon}
              </i>
              <span>{item.text}</span>
            </button>
          ),
        )}
      </div>
    </section>
  );
}

function silentItem(dq: DataQuality): Item {
  if (dq.sources_silent === 0) {
    return { key: "silent", icon: "✓", text: "No silent sources", severity: "ok" };
  }
  return {
    key: "silent",
    icon: "🔇",
    text: `${dq.sources_silent} source${dq.sources_silent === 1 ? "" : "s"} silent`,
    severity: "warn",
    to: "/sources",
  };
}

function staleItem(dq: DataQuality): Item {
  const n = dq.stale_sources.length;
  if (n === 0) return { key: "stale", icon: "✓", text: "No stale sources", severity: "ok" };
  return {
    key: "stale",
    icon: "⏳",
    text: `${n} stale source${n === 1 ? "" : "s"}`,
    severity: "warn",
    to: "/sources",
  };
}

function dropsItem(dq: DataQuality): Item {
  if (dq.drops_in_range === 0) {
    return { key: "drops", icon: "✓", text: "No drops in range", severity: "ok" };
  }
  return {
    key: "drops",
    icon: "▼",
    text: `${dq.drops_in_range} dropped in range`,
    severity: "warn",
    to: "/system",
  };
}

function samplingItem(dq: DataQuality): Item {
  if (!dq.sampling_in_effect) {
    return { key: "sampling", icon: "✓", text: "No sampling (counts exact)", severity: "ok" };
  }
  return { key: "sampling", icon: "◍", text: "Sampling in effect. Counts est.", severity: "info" };
}

function skewItem(dq: DataQuality): Item | null {
  if (dq.clock_skew_sources === 0) return null;
  return {
    key: "skew",
    icon: "⏱",
    text: `${dq.clock_skew_sources} source${dq.clock_skew_sources === 1 ? "" : "s"} with clock skew`,
    severity: "warn",
    to: "/sources",
  };
}

function revocationItem(dq: DataQuality): Item | null {
  if (dq.accepted_during_revocation_propagation === 0) return null;
  return {
    key: "revoke",
    icon: "⚠",
    text: `${dq.accepted_during_revocation_propagation} accepted during revocation propagation`,
    severity: "warn",
  };
}

function gapItem(dq: DataQuality): Item | null {
  const gaps = dq.gaps ?? [];
  if (gaps.length === 0) return null;
  return {
    key: "gap",
    icon: "⌁",
    text: `${gaps.length} sequence gap${gaps.length === 1 ? "" : "s"} in range`,
    severity: "warn",
    to: "/sources",
  };
}

/**
 * Coerce a possibly-absent or partial `DataQuality` into a complete one with honest-zero defaults, so
 * the strip renders (and reports) without a crash when the response omits `ingest_health` or a field.
 */
function normalizeDataQuality(dq: DataQuality | undefined | null): DataQuality {
  return {
    sources_silent: dq?.sources_silent ?? 0,
    stale_sources: dq?.stale_sources ?? [],
    drops_in_range: dq?.drops_in_range ?? 0,
    sampling_in_effect: dq?.sampling_in_effect ?? false,
    clock_skew_sources: dq?.clock_skew_sources ?? 0,
    accepted_during_revocation_propagation: dq?.accepted_during_revocation_propagation ?? 0,
    gaps: dq?.gaps ?? [],
  };
}
